import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../api/ApiRoutes";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatString } from "../../../utils/constants/stringUtils";
import { getApiErrorMessage } from "../../../utils/ErrorUtils";
import {
  extractJackpotAmountFromTags,
  JACKPOT_COLLECTION_CODE,
  JACKPOT_PROVIDER,
  jackpotGameKey,
  type JackpotConfig,
  type JackpotGameRow,
} from "../../../components/Configurations/casino-configuration/features/jackpotConfig";
import {
  fetchCollectionGames,
  type CollectionGame,
} from "../collections/useCollectionGames";

export const JACKPOT_CONFIG_QUERY_KEY = [
  "casinoConfiguration",
  "features",
  "jackpot",
] as const;

export type JackpotConfigApi = {
  enabled: boolean;
};

export type JackpotCatalogGame = {
  provider: string;
  gameId: string;
  name: string;
  isActive: boolean;
  image?: string;
};

function getInstance() {
  const token = JSON.parse(localStorage.getItem("token") ?? "null");
  return createPrivateAdminPanelInstance(token?.AccessToken);
}

function responseStatus(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null) return undefined;
  const status = (error as { response?: { status?: unknown } }).response
    ?.status;
  return typeof status === "number" ? status : undefined;
}

/**
 * Operator-facing copy from a casino-service envelope. Prefers
 * `error.message` when top-level `message` is null. Axios HTTP errors
 * (400) never reach {@link ensureSuccess}, so write callers wrap those too.
 */
function toJackpotApiError(error: unknown, fallback: string): Error {
  if (error instanceof Error && responseStatus(error) === undefined) {
    return error;
  }
  return new Error(getApiErrorMessage(error, fallback));
}

function ensureSuccess(body: unknown, fallback: string): void {
  if (
    body &&
    typeof body === "object" &&
    (body as Record<string, unknown>).success === false
  ) {
    throw new Error(
      getApiErrorMessage({ response: { data: body } }, fallback),
    );
  }
}

export function mapCollectionGameToJackpotRow(
  game: CollectionGame,
): JackpotGameRow | null {
  const amount = extractJackpotAmountFromTags(game.tags);
  // Skip members without a valid baseline amount so Save is not permanently blocked.
  if (amount === null || amount <= 0) return null;

  return {
    provider: String(game.provider ?? JACKPOT_PROVIDER),
    gameId: String(game.game_id ?? ""),
    name: String(game.name ?? game.game_id ?? ""),
    image:
      game.custom_image ||
      game.image ||
      game.external_image_url ||
      undefined,
    amount,
    isActive: Boolean(game.is_active),
  };
}

export function mapApiToJackpotEnabled(
  api: Partial<JackpotConfigApi> | null | undefined,
): boolean {
  return Boolean(api?.enabled);
}

export async function fetchJackpotEnabled(): Promise<boolean> {
  const instance = getInstance();
  const { data: body } = await instance.get(API_ROUTES.GET_JACKPOT_CONFIG);
  ensureSuccess(body, AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_FETCH_ERROR);
  return mapApiToJackpotEnabled(body?.data);
}

export async function saveJackpotEnabled(
  enabled: boolean,
): Promise<boolean> {
  const instance = getInstance();
  const { data: body } = await instance.put(API_ROUTES.PUT_JACKPOT_CONFIG, {
    enabled,
  });
  ensureSuccess(body, AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_SAVE_ERROR);
  return mapApiToJackpotEnabled(body?.data);
}

/** Page through special_jackpot members and project to domain rows. */
export async function fetchJackpotGames(): Promise<JackpotGameRow[]> {
  const pageSize = 100;
  let page = 1;
  let totalPages = 1;
  const rows: JackpotGameRow[] = [];

  while (page <= totalPages) {
    const result = await fetchCollectionGames(
      JACKPOT_COLLECTION_CODE,
      "all",
      undefined,
      page,
      pageSize,
    );
    for (const item of result.items) {
      const row = mapCollectionGameToJackpotRow(item);
      if (row && row.gameId) rows.push(row);
    }
    totalPages = result.meta?.total_pages ?? 1;
    page += 1;
    if (page > 50) break; // safety cap
  }
  return rows;
}

/** Rail search page size — matches gateway contract used by the Features UI. */
export const JACKPOT_RAIL_SEARCH_PAGE_SIZE = 50;

/**
 * Search special_jackpot members via collection games API.
 * `GET .../collections/special_jackpot/games?view=all&page=1&page_size=50&search=`
 *
 * Accepts raw input — trims before the request so direct callers stay safe.
 */
export async function searchJackpotGames(
  search: string,
): Promise<JackpotGameRow[]> {
  const q = search.trim();
  if (!q) return [];
  const result = await fetchCollectionGames(
    JACKPOT_COLLECTION_CODE,
    "all",
    q,
    1,
    JACKPOT_RAIL_SEARCH_PAGE_SIZE,
  );
  const rows: JackpotGameRow[] = [];
  for (const item of result.items) {
    const row = mapCollectionGameToJackpotRow(item);
    if (row?.gameId) rows.push(row);
  }
  return rows;
}

export function useJackpotGamesSearch(search: string) {
  const q = search.trim();
  return useQuery({
    queryKey: [...JACKPOT_CONFIG_QUERY_KEY, "search", q] as const,
    queryFn: () => searchJackpotGames(q),
    enabled: q.length > 0,
    staleTime: 30_000,
  });
}

/**
 * Load enable flag + rail. Enable failures propagate so the Features page
 * can show an error. Games-collection failure (e.g. flag-off 404) returns [].
 */
export async function fetchJackpotConfig(): Promise<JackpotConfig> {
  const enabled = await fetchJackpotEnabled();
  try {
    const games = await fetchJackpotGames();
    return { enabled, games };
  } catch {
    return { enabled, games: [] };
  }
}

export function useJackpotConfig() {
  return useQuery({
    queryKey: JACKPOT_CONFIG_QUERY_KEY,
    queryFn: fetchJackpotConfig,
    staleTime: 60_000,
  });
}

export async function addJackpotGame(input: {
  provider: string;
  gameId: string;
  amount: number;
}): Promise<void> {
  try {
    const instance = getInstance();
    // Gateway contract: amount is a decimal string (e.g. "4000"), not a JSON number.
    const { data: body } = await instance.post(API_ROUTES.POST_JACKPOT_GAME, {
      provider: input.provider,
      game_id: input.gameId,
      amount: String(input.amount),
    });
    ensureSuccess(body, AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_SAVE_ERROR);
  } catch (error) {
    throw toJackpotApiError(
      error,
      AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_SAVE_ERROR,
    );
  }
}

export async function patchJackpotGameAmount(input: {
  provider: string;
  gameId: string;
  amount: number;
}): Promise<void> {
  try {
    const instance = getInstance();
    const url = API_ROUTES.PATCH_JACKPOT_GAME.replace(
      "{provider}",
      encodeURIComponent(input.provider),
    ).replace("{game_id}", encodeURIComponent(input.gameId));
    const { data: body } = await instance.patch(url, {
      amount: String(input.amount),
    });
    ensureSuccess(body, AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_SAVE_ERROR);
  } catch (error) {
    throw toJackpotApiError(
      error,
      AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_SAVE_ERROR,
    );
  }
}

export async function deleteJackpotGame(input: {
  provider: string;
  gameId: string;
}): Promise<void> {
  try {
    const instance = getInstance();
    const url = API_ROUTES.DELETE_JACKPOT_GAME.replace(
      "{provider}",
      encodeURIComponent(input.provider),
    ).replace("{game_id}", encodeURIComponent(input.gameId));
    const { data: body } = await instance.delete(url);
    ensureSuccess(body, AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_SAVE_ERROR);
  } catch (error) {
    throw toJackpotApiError(
      error,
      AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_SAVE_ERROR,
    );
  }
}

/**
 * Persist staged enable + rail diff in sequence. Uses the member endpoints
 * because the backend has no single bulk put for the full rail.
 *
 * Game-level ops are best-effort: failures are collected and reported as a
 * partial-save error so operators know the server may be out of sync.
 */
export async function saveJackpotConfig(
  next: JackpotConfig,
  previous: JackpotConfig,
): Promise<JackpotConfig> {
  if (next.enabled !== previous.enabled) {
    await saveJackpotEnabled(next.enabled);
  }

  const prevByKey = new Map(
    previous.games.map((g) => [jackpotGameKey(g), g]),
  );
  const nextByKey = new Map(next.games.map((g) => [jackpotGameKey(g), g]));

  const gameOps: Array<() => Promise<void>> = [];

  for (const [key, prev] of Array.from(prevByKey.entries())) {
    if (!nextByKey.has(key)) {
      gameOps.push(() =>
        deleteJackpotGame({
          provider: prev.provider,
          gameId: prev.gameId,
        }),
      );
    }
  }

  for (const [key, row] of Array.from(nextByKey.entries())) {
    const prev = prevByKey.get(key);
    if (!prev) {
      gameOps.push(() =>
        addJackpotGame({
          provider: row.provider,
          gameId: row.gameId,
          amount: row.amount,
        }),
      );
    } else if (prev.amount !== row.amount) {
      gameOps.push(() =>
        patchJackpotGameAmount({
          provider: row.provider,
          gameId: row.gameId,
          amount: row.amount,
        }),
      );
    }
  }

  let succeeded = 0;
  let failed = 0;
  for (const op of gameOps) {
    try {
      await op();
      succeeded += 1;
    } catch {
      failed += 1;
    }
  }

  if (failed > 0) {
    const total = succeeded + failed;
    throw new Error(
      formatString(
        AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_PARTIAL_SAVE_ERROR,
        {
          succeeded: String(succeeded),
          total: String(total),
          failed: String(failed),
        },
      ),
    );
  }

  // Re-read server state so amounts/active flags match reality.
  return fetchJackpotConfig();
}

export function useSaveJackpotConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      next,
      previous,
    }: {
      next: JackpotConfig;
      previous: JackpotConfig;
    }) => saveJackpotConfig(next, previous),
    onSuccess: (saved) => {
      queryClient.setQueryData(JACKPOT_CONFIG_QUERY_KEY, saved);
    },
  });
}

/** Alea-only catalog for the add-game modal (paginated). */
export async function fetchAleaGamesCatalog(input: {
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<{ items: JackpotCatalogGame[]; total: number; hasNext: boolean }> {
  const instance = getInstance();
  const page = input.page ?? 1;
  const pageSize = input.pageSize ?? 50;
  const params: Record<string, unknown> = {
    page,
    page_size: pageSize,
    provider: JACKPOT_PROVIDER,
  };
  const search = input.search?.trim();
  if (search) params.search = search;

  const { data: body } = await instance.get(API_ROUTES.GET_ADMIN_GAMES, {
    params,
  });
  ensureSuccess(body, AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_CATALOG_ERROR);
  const data = body?.data;
  const rawItems = Array.isArray(data?.items) ? data.items : [];
  const items: JackpotCatalogGame[] = rawItems
    .map((row: Record<string, unknown>) => ({
      provider: String(row.provider ?? JACKPOT_PROVIDER).toLowerCase(),
      gameId: String(row.game_id ?? row.id ?? ""),
      name: String(row.name ?? row.game_name ?? row.game_id ?? ""),
      isActive: row.is_active !== false,
      image:
        (row.custom_image as string) ||
        (row.image as string) ||
        (row.external_image_url as string) ||
        undefined,
    }))
    .filter(
      (g: JackpotCatalogGame) =>
        g.gameId && g.provider === JACKPOT_PROVIDER,
    );

  const total = data?.total ?? data?.meta?.total ?? items.length;
  const totalPages =
    data?.meta?.total_pages ?? Math.max(1, Math.ceil(total / pageSize));
  return {
    items,
    total,
    hasNext: Boolean(data?.meta?.has_next) || page < totalPages,
  };
}
